import React from 'react';
import { Wallet, Loader2, AlertTriangle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import ThemeToggle from './ThemeToggle.jsx';
import { loginOps } from '@/lib/medplum.js';

// Super-admin sign-in for the ops console. Project-bound login to healther-ops
// (loginOps). No credential is shipped in the bundle — the human types it here.
function friendlyError(err) {
  const issues = (err && err.outcome && Array.isArray(err.outcome.issue)) ? err.outcome.issue : [];
  const text = [err && err.message, ...issues.map((i) => [i.code, i.details && i.details.text, i.diagnostics].filter(Boolean).join(' '))]
    .filter(Boolean).join(' | ');
  if (/missing projectId/i.test(text)) return 'Console misconfigured: set projectId in /ops-config.json to the healther-ops project id.';
  if ((err instanceof TypeError) || /failed to fetch|network|load failed/i.test(text)) return 'Could not reach the Medplum server. Check ops-config.json baseUrl and your connection.';
  if (/password|credential|invalid|incorrect|not found|unauthorized|login/i.test(text)) return 'Incorrect email or password.';
  if (/multiple/i.test(text)) return text;
  return 'Could not sign in. Please try again.';
}

export default function Login({ onSignedIn }) {
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');

  const onSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setError('');
    if (!email.trim() || !password) { setError('Enter your email and password.'); return; }
    setBusy(true);
    try {
      await loginOps({ email: email.trim().toLowerCase(), password });
      onSignedIn();
    } catch (err) {
      setError(friendlyError(err));
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/40 p-6">
      <div className="w-full max-w-[400px] cc-enter">
        <Card className="gap-0 rounded-2xl py-0 shadow-sm">
          <CardContent className="px-8 pb-7 pt-8">
            <div className="mb-6 flex flex-col items-center text-center">
              <div className="flex size-14 items-center justify-center rounded-2xl bg-primary text-primary-foreground">
                <Wallet className="size-7" />
              </div>
              <div className="mt-3.5 text-[20px] font-bold tracking-tight">Healther Ops</div>
              <div className="mt-1 text-[13px] font-medium text-muted-foreground">Cost &amp; AI observability console</div>
              <div className="mt-1 text-[11.5px] text-muted-foreground">Platform super-admin · healther-ops project</div>
            </div>

            {error && (
              <div className="mb-4 flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-[12.5px] font-medium text-destructive">
                <AlertTriangle className="mt-0.5 size-4 flex-none" /> {error}
              </div>
            )}

            <form onSubmit={onSubmit} noValidate>
              <div className="mb-3.5 flex flex-col gap-1.5">
                <Label htmlFor="email" className="text-xs text-muted-foreground">Email</Label>
                <Input id="email" type="email" autoComplete="username" autoFocus
                  className="h-11" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="ops@healther.tech" />
              </div>
              <div className="mb-5 flex flex-col gap-1.5">
                <Label htmlFor="pw" className="text-xs text-muted-foreground">Password</Label>
                <Input id="pw" type="password" autoComplete="current-password"
                  className="h-11" value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="Your password" />
              </div>
              <Button type="submit" disabled={busy} className="h-11 w-full text-[14.5px] font-semibold">
                {busy ? <><Loader2 className="size-4 animate-spin" /> Signing in…</> : 'Sign in'}
              </Button>
            </form>

            <div className="mt-4 flex items-center justify-between">
              <span className="text-[11.5px] text-muted-foreground">Authorized platform staff only</span>
              <ThemeToggle />
            </div>
          </CardContent>
        </Card>
        <div className="mt-4 text-center text-[11.5px] text-muted-foreground">
          Tip: append <code className="rounded bg-muted px-1 py-0.5">?mock=1</code> to preview with sample data (no login).
        </div>
      </div>
    </div>
  );
}
